import { useEffect, useState } from 'react';
import { IoIosArrowRoundUp, IoIosArrowRoundDown } from 'react-icons/io';
import Computer from '../../assets/Computer.png';
import Laptop from '../../assets/Laptop.png';
import Tablet from '../../assets/Tablet.png';
import Phone from '../../assets/Phone.png';
import api from '../../services/Api';

const deviceIcons = {
  Mac: Computer,
  Windows: Laptop,
  Android: Phone,
  iOS: Phone,
  Other: Tablet,
};

const allDeviceTypes = ['Mac', 'Windows', 'Android', 'iOS', 'Other'];

const DevicesOverview = () => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const currentStart = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const nextMonth = new Date(selectedYear, selectedMonth, 0);
      const currentEnd = `${selectedYear}-${String(selectedMonth).padStart(
        2,
        '0'
      )}-${nextMonth.getDate()}`;

      const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
      const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
      const prevStart = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
      const prevEnd = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${new Date(
        prevYear,
        prevMonth,
        0
      ).getDate()}`;

      try {
        const [currentRes, prevRes] = await Promise.all([
          api.get(`/analytics/device?start=${currentStart}&end=${currentEnd}`),
          api.get(`/analytics/device?start=${prevStart}&end=${prevEnd}`),
        ]);

        const currentData = currentRes.data || [];
        const prevData = prevRes.data || [];

        const totalCurrentUsers = currentData.reduce((sum, item) => sum + item.count, 0) || 1;
        const totalPrevUsers = prevData.reduce((sum, item) => sum + item.count, 0) || 1;

        const prevLookup = prevData.reduce((acc, item) => {
          acc[item._id] = item.count;
          return acc;
        }, {});

        const allDevicesMap = allDeviceTypes.reduce((acc, deviceType) => {
          acc[deviceType] = {
            name: deviceType,
            count: 0,
            icon: deviceIcons[deviceType],
            devicePercentage: '0.0',
            percentageChange: 0,
            percentDifference: '0.0',
          };
          return acc;
        }, {});

        currentData.forEach(item => {
          let deviceType;
          if (item._id.includes('Mac OS')) {
            deviceType = 'Mac';
          } else if (item._id.includes('Windows')) {
            deviceType = 'Windows';
          } else if (item._id.includes('Android')) {
            deviceType = 'Android';
          } else if (item._id.includes('iOS')) {
            deviceType = 'iOS';
          } else {
            deviceType = 'Other';
          }

          const prevCount = prevLookup[item._id] || 0;
          const percentageChange = prevCount ? ((item.count - prevCount) / prevCount) * 100 : 0;

          const devicePercentage = totalCurrentUsers
            ? ((item.count / totalCurrentUsers) * 100).toFixed(1)
            : '0.0';

          const prevDevicePercentage = totalPrevUsers
            ? ((prevCount / totalPrevUsers) * 100).toFixed(1)
            : '0.0';

          const percentDifference = (devicePercentage - prevDevicePercentage).toFixed(1);

          allDevicesMap[deviceType] = {
            name: deviceType,
            count: item.count,
            icon: deviceIcons[deviceType],
            devicePercentage,
            percentageChange,
            percentDifference,
          };
        });

        const formattedData = Object.values(allDevicesMap).sort((a, b) => b.count - a.count);

        setDevices(formattedData);
      } catch (error) {
        console.error('Error fetching device data:', error);
        const defaultDevices = allDeviceTypes.map(deviceType => ({
          name: deviceType,
          count: 0,
          icon: deviceIcons[deviceType],
          devicePercentage: '0.0',
          percentageChange: 0,
          percentDifference: '0.0',
        }));
        setDevices(defaultDevices);
      }
    };

    fetchData();
  }, [selectedMonth, selectedYear]);

  return (
    <div className="">
      <div className="flex justify-between items-center p-3 border-b-2 border-[#E5E7E8] font-nohemi">
        <p className=" font-medium text-sm text-[#191B1C]">Devices</p>
        <div className="flex space-x-2">
          <select
            className="border rounded p-1 text-sm text-[#4A5154] bg-[#F5F6F7]"
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2025, i))}
              </option>
            ))}
          </select>

          <select
            className="border rounded p-1 text-sm text-[#4A5154] bg-[#F5F6F7]"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}>
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Device Overview List */}
      <div className="flex flex-col px-3 py-3 mx-1">
        {devices.map(device => (
          <div key={device.name} className="h-[56px] my-2 flex flex-row">
            <img src={device.icon} className="h-[56px] w-[56px]" alt={device.name} />
            <div className="ml-4">
              <h1 className="text-base font-medium mb-1 text-[#191B1C]">{device.name}</h1>
              <p className="text-xs text-[#626C70] mb-1">
                {device.devicePercentage}% · {device.count} Users
              </p>
              <p
                className={`text-xs inline-flex ${
                  parseFloat(device.percentDifference) >= 0 ? 'text-[#0FAF62]' : 'text-[#E84646]'
                }`}>
                {parseFloat(device.percentDifference) >= 0 ? (
                  <>
                    <IoIosArrowRoundUp />
                    {device.percentDifference}% Increase
                  </>
                ) : (
                  <>
                    <IoIosArrowRoundDown />
                    {Math.abs(parseFloat(device.percentDifference))}% Decrease
                  </>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DevicesOverview;
